import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CaptureInput } from '../components/capture/CaptureInput';
import { SupplierStep, displayJobCode, projectCompany } from '../components/capture/SupplierStep';
import { CaptureDetails, EMPTY_DETAILS, grossOf, VAT_RATE } from '../components/capture/CaptureDetails';
import { useOrderSuppliers, accountFor } from '../components/portal/orders/SupplierAccount';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Camera, ArrowLeft, FileText, ClipboardList,
  Check, Loader2, Undo2, History, AlertTriangle, BookMarked, Package, Fuel, Search
} from 'lucide-react';

const STATES = { TYPE: 0, SUPPLIER: 1, COMPANY: 2, INPUT: 3, CONFIRM: 4, SENDING: 5, SENT: 6 };
const STEP_OF = { 0: 1, 1: 1, 2: 1, 3: 2, 4: 3 };

/** Compact job picker used on the diary quick-materials Confirm step — pick the
 *  job the photo belongs to. Same job list as the supplier step. */
function JobPicker({ api, company, selectedId, onPick }) {
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    api.get('/projects', { params: { status: 'Live', limit: 200 } }).then(r => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [api]);
  const shown = useMemo(() => {
    const list = (projects || []).filter(p => projectCompany(p) === company);
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(p => [p.project_ref, p.client_name, p.address_line1, displayJobCode(p.project_ref)].some(v => (v || '').toLowerCase().includes(needle)));
  }, [projects, q, company]);
  const label = company === 'CON' ? 'LUX Construction' : 'LUX Developments';
  return (
    <div data-testid="capture-confirm-job-step">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Which job? <span className="normal-case tracking-normal font-normal text-gray-600">· {label}</span></div>
      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search job, client or address…"
          className="w-full pl-9 h-11 rounded-lg bg-[#2A3240] border border-white/20 text-white text-sm focus:outline-none focus:border-[#F5A623]/50" data-testid="capture-confirm-job-search" />
      </div>
      <div className="max-h-[38vh] overflow-y-auto space-y-1.5 mt-2">
        {projects === null && <p className="text-xs text-gray-500 text-center py-4">Loading jobs…</p>}
        {shown.map(p => {
          const addr = [p.address_line1, p.town_city].filter(Boolean).join(', ') || p.client_name || '';
          return (
            <button key={p.id} type="button" onClick={() => onPick(p)} data-testid={`capture-confirm-job-${p.id}`}
              className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all active:scale-[0.98] ${selectedId === p.id ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/5 bg-[#2A3240] hover:border-[#F5A623]/30'}`}>
              <span className={`font-mono font-bold text-sm min-w-[72px] ${company === 'CON' ? 'text-blue-400' : 'text-[#F5A623]'}`}>{displayJobCode(p.project_ref)}</span>
              <span className="text-sm text-gray-200 truncate flex-1">{addr}</span>
            </button>
          );
        })}
        {projects && shown.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No live {label} jobs match — try the other company</p>}
      </div>
    </div>
  );
}

/**
 * Capture v2 — supplier first (account number), then the job, then photo /
 * gallery / "no paperwork", then optional typed details → Send.
 *   • Photo capture → stamped PDF → LiveCosts email + provisional ledger row.
 *   • Details-only  → provisional ledger row in the app only (no PDF/email).
 *   • Xero path     → company → photo → Send, no cost fields.
 * The supplier invoice posted later from the Cost Inbox covers the ledger row.
 */
export default function Capture() {
  const { user, api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const suppliers = useOrderSuppliers(api);

  const showHistoryLink = isAdmin && !location.pathname.startsWith('/portal');

  const preProjectId = searchParams.get('project_id');
  const preType = searchParams.get('type');
  const preCompany = searchParams.get('company');
  const fromDiary = searchParams.get('from_diary') === '1';
  const entryDiary = searchParams.get('entry') === 'diary';
  const returnPath = searchParams.get('return');

  const [state, setState] = useState(entryDiary ? STATES.TYPE : (preType === 'general' ? STATES.COMPANY : STATES.SUPPLIER));
  const [files, setFiles] = useState([]);
  const [detailsOnly, setDetailsOnly] = useState(false);
  const [captureType, setCaptureType] = useState(preType === 'general' ? 'general' : 'project');
  const [company, setCompany] = useState(preCompany || localStorage.getItem('capture_company') || 'DEV');
  const [supplier, setSupplier] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [lockedProject, setLockedProject] = useState(null);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [notes, setNotes] = useState('');
  const [extraction, setExtraction] = useState(null);
  const [inputBusy, setInputBusy] = useState(false);

  const [captureId, setCaptureId] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [undoSeconds, setUndoSeconds] = useState(60);
  const pollRef = useRef(null);

  const pollDelivery = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const startedAt = Date.now();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/captures/${id}`);
        const rec = r.data || {};
        setSendResult(prev => ({ ...(prev || {}), ...rec, id }));
        if (!rec.processing) { clearInterval(pollRef.current); pollRef.current = null; return; }
      } catch (_e) { /* keep polling until the window closes */ }
      if (Date.now() - startedAt > 45_000) {
        clearInterval(pollRef.current); pollRef.current = null;
        setSendResult(prev => ({ ...(prev || {}), processing: false, poll_timed_out: true }));
      }
    }, 2000);
  }, [api]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Launched from the Site Diary: the job is fixed.
  useEffect(() => {
    if (!preProjectId) return;
    api.get(`/projects/${preProjectId}`).then(res => {
      const p = res.data;
      const proj = { id: p.id, project_ref: p.project_ref, client_name: p.client_name, address_line1: p.address_line1, project_type: p.project_type };
      setLockedProject(proj); setSelectedProject(proj); setCompany(projectCompany(proj)); setCaptureType('project');
    }).catch(() => {});
  }, [api, preProjectId]);

  useEffect(() => {
    if (state !== STATES.SENT || undoSeconds <= 0) return;
    const timer = setTimeout(() => setUndoSeconds(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [state, undoSeconds]);

  // AI receipt read — starts as soon as a photo is added so the details screen is
  // already filled when the user gets there. Pre-fills only EMPTY fields; the supplier stays as chosen.
  const runExtraction = useCallback(async (file) => {
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (isPdf) { setExtraction({ status: 'unsupported' }); return; }
    setExtraction({ status: 'reading' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/captures/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
      if (!res.data?.ok) { setExtraction({ status: 'failed' }); return; }
      const f = res.data.fields || {};
      const gross = f.total_gross != null && f.total_gross > 0 ? Number(f.total_gross) : null;
      const vat = f.vat_amount != null && f.vat_amount > 0 ? Number(f.vat_amount) : null;
      const net = gross != null ? (vat != null && vat < gross ? gross - vat : gross / (1 + VAT_RATE / 100)) : null;
      const applied = {
        gross: gross != null ? gross.toFixed(2) : null,
        net: net != null ? net.toFixed(2) : null,
        reference: f.reference || null,
        invoice_date: f.date || null,
        description: f.line_summary || null,
      };
      setDetails(prev => ({
        ...prev,
        gross: prev.net === '' && prev.gross === '' && applied.gross ? applied.gross : prev.gross,
        net: prev.net === '' && applied.net ? applied.net : prev.net,
        reference: prev.reference || applied.reference || '',
        invoice_date: prev.invoice_date || applied.invoice_date || '',
        description: prev.description || applied.description || '',
      }));
      if (f.supplier_name) setSupplier(prev => (prev?.none && !prev.name ? { ...prev, name: f.supplier_name } : prev));
      setExtraction({ status: 'done', fields: f, supplier_match: res.data.supplier_match || null, applied });
    } catch {
      setExtraction({ status: 'failed' });
    }
  }, [api]);

  const extractedFor = useRef(null);
  useEffect(() => {
    const first = files[0];
    if (captureType !== 'project' || detailsOnly || entryDiary || !first || extractedFor.current === first) return;
    extractedFor.current = first;
    runExtraction(first);
  }, [files, captureType, detailsOnly, runExtraction]);

  const accountNo = supplier?.curated ? accountFor(supplier.curated, company) : null;
  const canContinueSupplier = !!supplier && !!selectedProject;

  // Xero receipts: always confirm the company with the logos — it decides which Xero inbox gets the email.
  const handleXero = () => { setCaptureType('general'); setSupplier(null); setSelectedProject(lockedProject); setState(STATES.COMPANY); };
  // Diary entry (Feature E): the type tiles choose the route, then both go to Company → Photo.
  const pickType = (type) => { setCaptureType(type); setSupplier(null); setSelectedProject(null); setState(STATES.COMPANY); };
  const handleCompanySelect = (comp) => { setCompany(comp); setState(STATES.INPUT); };
  const handleCompanyToggle = (comp) => {
    setCompany(comp); localStorage.setItem('capture_company', comp);
    if (selectedProject && !lockedProject && projectCompany(selectedProject) !== comp) setSelectedProject(null);
  };
  const handleDetailsOnly = () => { setDetailsOnly(true); setFiles([]); setExtraction(null); extractedFor.current = null; setState(STATES.CONFIRM); };

  const handleSend = async () => {
    setState(STATES.SENDING);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('capture_type', captureType);
      formData.append('company', company);
      if (captureType === 'project' && selectedProject) formData.append('linked_project_id', selectedProject.id);
      if (notes.trim()) formData.append('notes', notes.trim());

      if (captureType === 'project' && details.category && !entryDiary) {
        formData.append('category', details.category);
        if (supplier?.supplier_id) formData.append('supplier_id', supplier.supplier_id);
        if (supplier?.name?.trim()) formData.append('supplier_name', supplier.name.trim());
        if (supplier?.order_supplier_id) formData.append('order_supplier_id', supplier.order_supplier_id);
        if (accountNo) formData.append('supplier_account', accountNo);
        const gross = grossOf(details);
        if (gross > 0) formData.append('value', gross.toFixed(2));
        if (details.net !== '' && parseFloat(details.net) > 0) formData.append('net_value', String(parseFloat(details.net).toFixed(2)));
        formData.append('vat_rate', String(VAT_RATE));
        if (details.description.trim()) formData.append('description', details.description.trim());
        if (details.reference.trim()) formData.append('reference', details.reference.trim());
        if (details.invoice_date) formData.append('invoice_date', details.invoice_date);
      }

      const uploadRes = await api.post('/captures/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const id = uploadRes.data.id;
      setCaptureId(id);
      setSendResult(uploadRes.data);
      setUndoSeconds(60);
      setState(STATES.SENT);
      if (uploadRes.data.processing) pollDelivery(id);

      if (fromDiary && selectedProject) {
        try {
          await api.post('/diary/entries/capture-companion', {
            project_id: selectedProject.id, capture_id: id,
            job_reference: uploadRes.data?.job_reference || selectedProject.project_ref,
            captured_at: uploadRes.data?.captured_at || new Date().toISOString(),
            photo_web_url: uploadRes.data?.photo_web_url,
            title: details.description.trim() || notes.trim() || null,
          });
        } catch (_e) { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setState(STATES.CONFIRM);
    }
  };

  const handleUndo = async () => {
    if (!captureId) return;
    try { await api.delete(`/captures/${captureId}`); toast.success('Capture undone'); resetFlow(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Undo failed'); }
  };

  const resetFlow = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setFiles([]); setDetailsOnly(false); setCaptureType(entryDiary ? 'general' : (preType === 'general' ? 'general' : 'project'));
    setCompany(lockedProject ? projectCompany(lockedProject) : (preCompany || localStorage.getItem('capture_company') || 'DEV'));
    setSupplier(null); setSelectedProject(lockedProject); setDetails(EMPTY_DETAILS); setNotes('');
    setCaptureId(null); setSendResult(null); setUndoSeconds(60); setExtraction(null); extractedFor.current = null;
    setState(entryDiary ? STATES.TYPE : (preType === 'general' ? STATES.COMPANY : STATES.SUPPLIER));
  };

  const goBack = () => {
    if (state === STATES.COMPANY) { if (entryDiary) setState(STATES.TYPE); else { setCaptureType('project'); setState(STATES.SUPPLIER); } }
    else if (state === STATES.INPUT) setState(captureType === 'general' || entryDiary ? STATES.COMPANY : STATES.SUPPLIER);
    else if (state === STATES.CONFIRM) { setDetailsOnly(false); setState(STATES.INPUT); }
  };

  const companyName = company === 'DEV' ? 'Lux Developments' : 'Lux Construction';
  const jobCode = selectedProject ? displayJobCode(selectedProject.project_ref) : null;
  const destinationSummary = () => {
    if (captureType === 'general') return `Sending to Xero — ${companyName} (general / overhead)`;
    if (entryDiary) return selectedProject ? `Filing to ${jobCode} — ${companyName} · the supplier invoice covers it in the Cost Inbox` : 'Pick the job to file this against';
    if (detailsOnly) return `Logging to ${jobCode} in the app only — nothing sent to LiveCosts`;
    return `Sending to LiveCosts — ${companyName} — ${jobCode} ${selectedProject?.client_name || ''}`;
  };

  if (!user?.capture_enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Card className="bg-[#2A3240] border-white/10 p-8 text-center max-w-md">
          <Camera className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-white font-medium">Capture access not enabled</p>
          <p className="text-gray-400 text-sm mt-2">Ask an admin to enable Capture for your account.</p>
        </Card>
      </div>
    );
  }

  const SummaryStrip = () => (
    <div className="rounded-xl bg-[#1E2530] border border-white/10 px-3 py-2 flex items-center gap-3 text-sm" data-testid="capture-summary-strip">
      {captureType === 'general' ? (
        <><img src={company === 'DEV' ? '/logos/lux-developments.png' : '/logos/lux-construction.png'} alt="" className="h-5 object-contain" /><span className="text-gray-300">Xero · {companyName}</span></>
      ) : entryDiary ? (
        <>
          <span className="font-mono font-bold text-[#F5A623]">{jobCode || 'Materials'}</span>
          <span className="text-gray-300 truncate">{companyName}{files.length ? ` · ${files.length} file${files.length > 1 ? 's' : ''}` : ''}</span>
        </>
      ) : (
        <>
          <span className="font-mono font-bold text-[#F5A623]">{jobCode}</span>
          <span className="text-gray-300 truncate">{supplier?.name || (supplier?.none ? 'Supplier from receipt' : 'No supplier')}{files.length ? ` · ${files.length} file${files.length > 1 ? 's' : ''}` : detailsOnly ? ' · details only' : ''}</span>
          {accountNo && <span className="ml-auto font-mono font-bold text-[#F5A623]" data-testid="capture-summary-account">{accountNo}</span>}
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-[60vh] max-w-2xl mx-auto p-4 md:p-6" data-testid="capture-page">
      <div className="flex items-center justify-between mb-4">
        {state > STATES.SUPPLIER && state < STATES.SENDING ? (
          <button onClick={goBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        ) : (
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><Camera className="w-5 h-5 text-[#F5A623]" /> New Capture</h1>
        )}
        <div className="flex items-center gap-3">
          {state < STATES.SENDING && <span className="text-[10px] uppercase tracking-wider text-gray-500" data-testid="capture-step">Step {STEP_OF[state]} of 3</span>}
          {showHistoryLink && state === STATES.SUPPLIER && (
            <button onClick={() => navigate('/captures/history')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#F5A623] transition-colors" data-testid="view-history-link">
              <History className="w-4 h-4" /> View History
            </button>
          )}
        </div>
      </div>

      {state === STATES.SUPPLIER && (
        <div className="space-y-5">
          <SupplierStep api={api} suppliers={suppliers} company={company} onCompany={handleCompanyToggle} supplier={supplier} project={lockedProject ? null : selectedProject} lockedProject={lockedProject}
            onSupplier={setSupplier}
            onProject={(p) => { setSelectedProject(p); setCompany(projectCompany(p)); }}
            onXero={handleXero} />
          <div className={`sticky z-10 pt-2 bg-gradient-to-t from-[#1E2530] via-[#1E2530] to-transparent ${location.pathname.startsWith('/portal') ? 'bottom-[76px]' : 'bottom-2'}`}>
            <Button onClick={() => setState(STATES.INPUT)} disabled={!canContinueSupplier}
              className="w-full py-5 text-base bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold rounded-xl disabled:opacity-40" data-testid="capture-continue-btn">
              {canContinueSupplier ? 'Continue → take photo' : !supplier ? 'Pick a supplier to continue' : 'Pick the job to continue'}
            </Button>
          </div>
        </div>
      )}

      {state === STATES.TYPE && (
        <div className="space-y-4" data-testid="capture-type-step">
          <h2 className="text-lg font-bold text-white">What are you capturing?</h2>
          <p className="text-sm text-gray-400 -mt-2">Fuel and overheads go straight to Xero — no job needed. Materials get logged to a job and the supplier invoice follows in the Cost Inbox.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={() => pickType('general')} data-testid="type-general"
              className="p-8 rounded-xl border border-amber-500/30 hover:bg-amber-500/10 flex flex-col items-center justify-center gap-3 transition-all active:scale-[0.97]">
              <Fuel className="w-14 h-14 text-[#F5A623]" />
              <span className="text-sm font-semibold text-white">Fuel / overhead</span>
              <span className="text-[11px] text-gray-400">Straight to Xero — no job</span>
            </button>
            <button onClick={() => pickType('project')} data-testid="type-project"
              className="p-8 rounded-xl border border-emerald-500/30 hover:bg-emerald-500/10 flex flex-col items-center justify-center gap-3 transition-all active:scale-[0.97]">
              <Package className="w-14 h-14 text-emerald-300" />
              <span className="text-sm font-semibold text-white">Materials</span>
              <span className="text-[11px] text-gray-400">Logged to a job · Cost Inbox</span>
            </button>
          </div>
        </div>
      )}

      {state === STATES.COMPANY && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-white">{captureType === 'general' ? "Which company's Xero is this for?" : 'Which company is this for?'}</h2>
          <p className="text-sm text-gray-400 -mt-2">{captureType === 'general' ? "Fuel, tools and general receipts go straight to that company's Xero inbox — no cost details needed. Tap the right logo." : 'Pick the company, then take a photo and choose the job.'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[['DEV', 'Lux Developments', '/logos/lux-developments.png', 'border-blue-500/30 hover:bg-blue-500/10'], ['CON', 'Lux Construction', '/logos/lux-construction.png', 'border-purple-500/30 hover:bg-purple-500/10']].map(([code, name, logo, tone]) => (
              <button key={code} onClick={() => handleCompanySelect(code)} className={`p-8 rounded-xl border ${tone} flex flex-col items-center justify-center gap-3 transition-all active:scale-[0.97]`} data-testid={`company-${code}`}>
                <img src={logo} alt={name} className="h-16 object-contain" />
                <span className="text-sm font-semibold text-white">{name}</span>
                <span className="text-[11px] text-gray-400">{captureType === 'general' ? `Send to ${name} Xero` : 'Log against this company'}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {state === STATES.INPUT && (
        <div className="space-y-4">
          <SummaryStrip />
          <h2 className="text-lg font-bold text-white">{captureType === 'general' ? 'Photo of the receipt' : 'Receipt, delivery note or the goods'}</h2>
          <CaptureInput files={files} setFiles={setFiles} onContinue={() => setState(STATES.CONFIRM)} onBusy={setInputBusy} />
          {captureType === 'project' && !entryDiary && files.length > 0 && extraction && extraction.status !== 'unsupported' && (
            <p className="text-[11px] text-gray-400 flex items-center gap-1.5" data-testid="capture-step2-reading">
              {extraction.status === 'reading' ? <><Loader2 className="w-3 h-3 animate-spin text-[#F5A623]" /> Reading the receipt in the background — the details will be filled in on the next step.</>
                : extraction.status === 'done' ? <><Check className="w-3 h-3 text-emerald-300" /> Receipt read{extraction.fields?.total_gross ? ` · £${Number(extraction.fields.total_gross).toFixed(2)}` : ''}{extraction.fields?.supplier_name ? ` · ${extraction.fields.supplier_name}` : ''}</>
                : <><AlertTriangle className="w-3 h-3 text-gray-500" /> Couldn't read the receipt — you can type the details next.</>}
            </p>
          )}
          {captureType === 'project' && !entryDiary && files.length === 0 && !inputBusy && (
            <button onClick={handleDetailsOnly} data-testid="capture-details-only-btn"
              className="w-full rounded-xl border border-dashed border-white/20 px-4 py-3 text-left hover:border-[#F5A623]/50 transition-colors">
              <div className="flex items-center gap-2 text-sm font-semibold text-white"><ClipboardList className="w-4 h-4 text-[#F5A623]" /> No paperwork — enter details only</div>
              <div className="text-[11px] text-gray-400 mt-1">Counter didn't give a note? Type what you collected and the value. It's logged against the job in the app only; the supplier invoice covers it later.</div>
            </button>
          )}
        </div>
      )}

      {state === STATES.CONFIRM && (
        <div className="space-y-5">
          <SummaryStrip />
          {captureType === 'general' ? (
            <Card className="bg-[#2A3240] border-white/10 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-gray-400" /><span className="text-gray-300">{files.length} file{files.length > 1 ? 's' : ''} will be converted to a single PDF</span></div>
              <p className="text-white text-sm font-medium">{destinationSummary()}</p>
            </Card>
          ) : entryDiary ? (
            <JobPicker api={api} company={company} selectedId={selectedProject?.id} onPick={setSelectedProject} />
          ) : (
            <CaptureDetails value={details} onChange={setDetails} extraction={extraction} detailsOnly={detailsOnly} allowPhotoOnly={!detailsOnly && !fromDiary}
              supplierName={supplier?.none ? null : supplier?.name}
              supplierField={supplier?.none ? { value: supplier.name || '', onChange: (v) => setSupplier(prev => ({ ...prev, name: v })) } : null} />
          )}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Materials for kitchen first fix"
              className="w-full h-16 rounded-lg bg-[#1E2530] border border-white/10 text-white text-sm p-3 resize-none focus:outline-none focus:border-[#F5A623]/50" data-testid="notes-input" />
          </div>
          <p className="text-xs text-gray-400" data-testid="capture-destination">{destinationSummary()}</p>
          <Button onClick={handleSend} disabled={extraction?.status === 'reading' || (entryDiary && captureType === 'project' && !selectedProject) || (detailsOnly && !details.description.trim() && grossOf(details) <= 0 && !details.reference.trim())}
            className="w-full py-5 text-base bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold rounded-xl disabled:opacity-60" data-testid="send-btn">
            {extraction?.status === 'reading' ? 'Reading receipt…' : entryDiary && captureType === 'project' ? (selectedProject ? 'File to job' : 'Pick a job first') : detailsOnly ? 'Log to job' : 'Send'}
          </Button>
        </div>
      )}

      {state === STATES.SENDING && (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
          <Loader2 className="w-10 h-10 text-[#F5A623] animate-spin" />
          <p className="text-white font-medium">{detailsOnly ? 'Logging…' : 'Sending...'}</p>
          {!detailsOnly && <p className="text-gray-400 text-sm">Uploading, generating PDF, and emailing</p>}
        </div>
      )}

      {state === STATES.SENT && (() => {
        const ledgerOnly = !!sendResult?.details_only;
        const inFlight = !ledgerOnly && !!sendResult?.processing;
        const wasSent = sendResult?.sent_status === 'sent';
        const tone = ledgerOnly ? 'ledger' : inFlight ? 'processing' : wasSent ? 'ok' : 'not-sent';
        const gross = grossOf(details);
        return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-5 text-center" data-testid={`sent-status-${tone}`}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${wasSent || ledgerOnly ? 'bg-green-500/20' : inFlight ? 'bg-white/10' : 'bg-amber-500/20'}`}>
            {inFlight ? <Loader2 className="w-8 h-8 text-[#F5A623] animate-spin" /> : ledgerOnly ? <BookMarked className="w-8 h-8 text-green-400" /> : wasSent ? <Check className="w-8 h-8 text-green-400" /> : <AlertTriangle className="w-8 h-8 text-amber-400" />}
          </div>
          <div className="max-w-sm">
            <p className="text-white text-xl font-bold">{ledgerOnly ? 'Logged to the job' : inFlight ? 'Saved — filing & sending…' : wasSent ? 'Sent successfully' : 'Saved — not sent yet'}</p>
            {ledgerOnly ? (
              <p className="text-gray-400 text-sm mt-2">Nothing was sent to LiveCosts. When {supplier?.name || 'the supplier'}'s invoice is posted from the Cost Inbox it covers this entry.</p>
            ) : inFlight ? (
              <p className="text-gray-400 text-sm mt-2">The receipt is stored{sendResult?.cost_ledger_row_id ? ' and logged' : ''}. Filing to OneDrive and emailing {sendResult?.destination_type === 'xero' ? 'Xero' : 'LiveCosts'} is finishing in the background — you can carry on.</p>
            ) : wasSent ? (
              <p className="text-gray-400 text-sm mt-2">{destinationSummary()}</p>
            ) : (
              <>
                <p className="text-gray-300 text-sm mt-2">The receipt is filed{sendResult?.cost_ledger_row_id ? ' and logged to the ledger' : ''}, but the email to {sendResult?.destination_type === 'xero' ? 'Xero' : 'LiveCosts'} {sendResult?.poll_timed_out ? "hasn't been confirmed yet" : "hasn't gone"}.</p>
                <p className="text-amber-300/90 text-xs mt-2" data-testid="sent-failure-reason">{sendResult?.poll_timed_out ? 'Still sending — check Captures history in a minute.' : (sendResult?.failure_reason || 'Sending did not complete.')}</p>
                <p className="text-gray-500 text-xs mt-2">An admin can resend it from Captures → Not sent. Nothing to redo on site.</p>
              </>
            )}
            {sendResult?.cost_ledger_row_id && (
              <p className="inline-flex items-center gap-1.5 mt-3 text-[12px] px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" data-testid="sent-ledger-summary">
                <Check className="w-3.5 h-3.5" />
                {gross > 0 ? `£${gross.toFixed(2)} logged to ${details.category}` : `Logged to ${details.category} — value pending`}{jobCode ? ` · ${jobCode}` : ''}
              </p>
            )}
          </div>

          {undoSeconds > 0 && (
            <div className="space-y-2">
              <Button onClick={handleUndo} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="undo-btn">
                <Undo2 className="w-4 h-4 mr-2" /> Undo ({undoSeconds}s)
              </Button>
              <p className="text-gray-500 text-[10px]">{wasSent ? 'Removes it from history and the ledger. Cannot recall the sent email.' : 'Removes this capture from history and the ledger.'}</p>
            </div>
          )}

          <div className="flex gap-3 mt-4">
            {returnPath ? (
              <Button onClick={() => navigate(returnPath)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="return-to-diary-btn">Back to Site Diary</Button>
            ) : (
              <Button onClick={resetFlow} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="capture-another-btn"><Camera className="w-4 h-4 mr-2" /> Capture another</Button>
            )}
            <Button onClick={() => returnPath ? navigate(returnPath) : navigate(-1)} variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5" data-testid="done-btn">Done</Button>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
